import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { LanguageService } from '@core/service/language/language.service';
import { AlertService } from '@core/service/alert/alert.service';
import { AuthGateService } from '@core/service/auth-gate/auth-gate.service';
import {
  AuthState,
  FavoriteState,
  HomeState,
  LoadCategories,
  LoadFavoriteIds,
  LoadIdeasFilterLookups,
  LoadQuoteCount,
  Logout,
  ProductState,
  QuoteListState,
} from '@data/state';
import { AppLanguage } from '@data/enum';
import { ICategoryViewModel, ILookupOption } from '@data/schema';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, NgOptimizedImage, TranslatePipe],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class HeaderComponent implements OnInit {
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _alert = inject(AlertService);
  private readonly _translate = inject(TranslateService);
  private readonly _authGate = inject(AuthGateService);
  readonly language = inject(LanguageService);

  readonly topLevelCategories = this._store.selectSignal(HomeState.topLevelCategories);
  readonly categoriesLoading = this._store.selectSignal(HomeState.categoriesLoading);
  readonly occasions = this._store.selectSignal(ProductState.occasions);
  readonly featuredOccasions = this._store.selectSignal(ProductState.featuredOccasions);
  readonly isAuthenticated = this._store.selectSignal(AuthState.isAuthenticated);
  readonly fullName = this._store.selectSignal(AuthState.fullName);
  readonly favoriteIds = this._store.selectSignal(FavoriteState.ids);
  readonly quotesCount = this._store.selectSignal(QuoteListState.headerQuoteCount);

  readonly favoritesCount = computed(() => this.favoriteIds().length);

  readonly nonFeaturedOccasions = computed(() => {
    const featuredIds = new Set(this.featuredOccasions().map((o) => o.id));
    return this.occasions().filter((o) => !featuredIds.has(o.id));
  });

  readonly categoriesOpen = signal(false);
  readonly ideasOpen = signal(false);
  readonly moreOpen = signal(false);
  readonly languageMenuOpen = signal(false);
  readonly profileMenuOpen = signal(false);

  readonly mobileMenuOpen = signal(false);
  readonly mobileCategoriesOpen = signal(false);
  readonly mobileIdeasOpen = signal(false);

  private readonly _currentUrl = toSignal(
    this._router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this._router.url },
  );

  readonly isAuthRoute = computed(() => this._currentUrl().startsWith('/auth'));

  constructor() {
    effect(() => {
      if (this.isAuthenticated())
        this._store.dispatch([new LoadFavoriteIds(), new LoadQuoteCount()]);
    });
  }

  ngOnInit(): void {
    this._store.dispatch(new LoadCategories());
    this._store.dispatch(new LoadIdeasFilterLookups());
  }

  toggleCategories(event: Event): void {
    event.stopPropagation();
    this.ideasOpen.set(false);
    this.moreOpen.set(false);
    this.languageMenuOpen.set(false);
    this.profileMenuOpen.set(false);
    this.categoriesOpen.update((open) => !open);
  }

  toggleIdeas(event: Event): void {
    event.stopPropagation();
    this.categoriesOpen.set(false);
    this.moreOpen.set(false);
    this.languageMenuOpen.set(false);
    this.profileMenuOpen.set(false);
    this.ideasOpen.update((open) => !open);
  }

  toggleMore(event: Event): void {
    event.stopPropagation();
    this.categoriesOpen.set(false);
    this.ideasOpen.set(false);
    this.languageMenuOpen.set(false);
    this.profileMenuOpen.set(false);
    this.moreOpen.update((open) => !open);
  }

  toggleLanguageMenu(event: Event): void {
    event.stopPropagation();
    this.categoriesOpen.set(false);
    this.ideasOpen.set(false);
    this.moreOpen.set(false);
    this.profileMenuOpen.set(false);
    this.languageMenuOpen.update((open) => !open);
  }

  toggleProfileMenu(event: Event): void {
    event.stopPropagation();
    this.categoriesOpen.set(false);
    this.ideasOpen.set(false);
    this.moreOpen.set(false);
    this.languageMenuOpen.set(false);
    this.profileMenuOpen.update((open) => !open);
  }

  selectLanguage(lang: AppLanguage): void {
    this.language.setLanguage(lang);
    this.languageMenuOpen.set(false);
  }

  toggleMobileMenu(): void {
    const next = !this.mobileMenuOpen();
    this.mobileMenuOpen.set(next);
    document.body.style.overflow = next ? 'hidden' : '';
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
    this.mobileCategoriesOpen.set(false);
    this.mobileIdeasOpen.set(false);
    document.body.style.overflow = '';
  }

  toggleMobileCategories(): void {
    this.mobileCategoriesOpen.update((open) => !open);
  }

  toggleMobileIdeas(): void {
    this.mobileIdeasOpen.update((open) => !open);
  }

  goToAllProducts(): void {
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products']);
  }

  selectCategory(category: ICategoryViewModel): void {
    this.categoriesOpen.set(false);
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { categoryId: category.id } });
  }

  selectOccasion(occasion: ILookupOption): void {
    this.ideasOpen.set(false);
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { occasionId: occasion.id } });
  }

  quickFilterExpress(): void {
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { expressDelivery: 3 } });
  }

  quickFilterUaeStock(): void {
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { isInUaeStock: true } });
  }

  quickFilterSustainable(): void {
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { isSustainable: true } });
  }

  quickFilterFastResponse(): void {
    this.moreOpen.set(false);
    if (!this._authGate.requireAuth()) return;
    this._router.navigate(['/products'], { queryParams: { maxResponseTime: 60 } });
  }

  notImplemented(key: string): void {
    this.moreOpen.set(false);
    this._translate.get(key).subscribe((message) => this._alert.info(message));
  }

  logout(): void {
    this.profileMenuOpen.set(false);
    this.closeMobileMenu();
    this._store.dispatch(new Logout());
    this._router.navigateByUrl('/');
  }

  onDocumentClick(event: Event): void {
    if (this._elementRef.nativeElement.contains(event.target as Node)) return;
    this.categoriesOpen.set(false);
    this.ideasOpen.set(false);
    this.moreOpen.set(false);
    this.languageMenuOpen.set(false);
    this.profileMenuOpen.set(false);
  }
}
